import { inject, NgZone } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import Uppy from '@uppy/core';
import XHRUpload from '@uppy/xhr-upload';
import { AgentSessionStateService } from 'AgentApp/ts/services/agent-session-state.service';
import { AjaxService } from 'NewAngular/services/core/ajax.service';
import { UrlsService } from 'NewAngular/services/core/urls.service';
import { PageContextClientTool } from 'AgentApp/ts/client-tools/page-context.client-tool';
import * as i0 from "@angular/core";
const UNKNOWN_ERROR_MESSAGE = 'An unexpected error occurred. Please reach out to [support@ycharts.com](mailto:support@ycharts.com) for help.';
const MAX_FILE_SIZE_IN_BYTES = 33554433; // 32MB
const SSE_EVENT_TYPES = [
    'text_delta',
    'thinking_delta',
    'tool_call_start',
    'tool_call_stop',
    'done',
    'error',
    'error_max_sessions',
    'error_usage_limit',
    'requesting_confirmation',
    'interrupted',
    'interaction_id',
    'client_tool_request',
];
export class AgentTransportService {
    constructor() {
        this._ngZone = inject(NgZone);
        this._urlsService = inject(UrlsService);
        this._ajaxService = inject(AjaxService);
        this._sessionState = inject(AgentSessionStateService);
        this._pageContextClientTool = inject(PageContextClientTool);
        this._eventSources = new Map();
        this._interactionIds = new Map();
        this._lastClientContext = new Map();
        this._sessionProviders = new Map();
        this._pendingConfirmations = new Map();
        this._isFileUploading = false;
        this._uppy = new Uppy({
            autoProceed: false,
            restrictions: {
                maxFileSize: MAX_FILE_SIZE_IN_BYTES,
                maxNumberOfFiles: 5,
                allowedFileTypes: ['image/*', '.pdf', '.xlsx', '.csv', '.txt'],
            },
        });
        this.initUppy('anthropic');
    }
    get isFileUploading() {
        return this._isFileUploading;
    }
    initUppy(provider) {
        const existing = this._uppy.getPlugin('XHRUpload');
        if (existing) {
            existing.setOptions({
                endpoint: this._urlsService.getUrl(this._urlsService.AGENT.FILE_UPLOAD, { provider }),
            });
        }
        else {
            this._uppy.use(XHRUpload, {
                endpoint: this._urlsService.getUrl(this._urlsService.AGENT.FILE_UPLOAD, { provider }),
                formData: true,
                fieldName: 'files',
                bundle: true,
            });
        }
    }
    getSmartCompareContext(securityIds) {
        const url = this._urlsService.getUrl(this._urlsService.AGENT.SMART_COMPARE_CONTEXT);
        return this._ajaxService.getDataObserveBody(url, {
            security_ids: securityIds.join(','),
        });
    }
    async createSession(message, files = [], options = {}) {
        const messageFiles = files.map((file) => ({
            attachment_url: file.name,
            attachment_mime_type: file.type,
            external_file_id: '',
        }));
        // Background invocations (e.g., scheduled task runs) provide a name and bypass the
        // pending-message UI signal — the user isn't waiting at the chat input.
        const isBackground = options.agentPromptId !== undefined;
        if (!isBackground) {
            this._sessionState.pendingNewSessionMessage.set({ text: message, files: messageFiles });
        }
        try {
            const url = this._urlsService.getUrl(this._urlsService.AGENT.SESSIONS);
            const body = { message };
            if (options.agentPromptId !== undefined) {
                body.agent_prompt_id = options.agentPromptId;
            }
            if (options.name !== undefined) {
                body.name = options.name;
            }
            const response = await firstValueFrom(this._ajaxService.postJsonDataObserveBody(url, body));
            this._sessionProviders.set(response.agent_session_id, response.provider);
            return {
                id: response.agent_session_id,
                name: response.name,
                icon: response.icon,
                provider: response.provider,
            };
        }
        finally {
            if (!isBackground) {
                this._sessionState.pendingNewSessionMessage.set(null);
            }
        }
    }
    async getSession(agentSessionId) {
        const url = this._urlsService.getUrl(this._urlsService.AGENT.SESSION_DETAIL, { agentSessionId });
        const response = await firstValueFrom(this._ajaxService.getDataObserveBody(url));
        this._sessionProviders.set(response.session.id, response.session.provider);
        return response.session;
    }
    async _getSessionProvider(sessionId) {
        const cached = this._sessionProviders.get(sessionId);
        if (cached) {
            return cached;
        }
        const session = await this.getSession(sessionId);
        return session.provider;
    }
    async getActiveSessions(userId, agentSessionId) {
        let url = `${this._getBaseUrl()}/agent/active_sessions?user_id=${userId}`;
        if (agentSessionId !== undefined) {
            url += `&agent_session_id=${agentSessionId}`;
        }
        try {
            const response = await fetch(url, { credentials: 'include' });
            const data = await response.json();
            return data.active_sessions ?? {};
        }
        catch {
            return {};
        }
    }
    hasActiveConnection(sessionId) {
        const es = this._eventSources.get(sessionId);
        return !!es && es.readyState !== EventSource.CLOSED;
    }
    setInteractionId(sessionId, interactionId) {
        this._interactionIds.set(sessionId, interactionId);
    }
    async reconnectToSession(sessionId, userId, interactionId, lastMessageStreamId, clientContext) {
        this._closeEventSource(sessionId);
        this._sessionState.markReconnecting(sessionId);
        const params = new URLSearchParams({
            agent_session_id: sessionId.toString(),
            user_id: userId.toString(),
            interaction_id: interactionId,
        });
        if (lastMessageStreamId) {
            params.set('last_message_stream_id', lastMessageStreamId);
        }
        if (clientContext) {
            params.set('client_context', JSON.stringify(clientContext));
        }
        params.set('provider', await this._getSessionProvider(sessionId));
        const sseUrl = `${this._getBaseUrl()}/agent/new?${params.toString()}`;
        this._openEventSource(sessionId, sseUrl, true);
    }
    async sendUserMessage(sessionId, text, files = [], clientContext = undefined) {
        const messageFiles = files.map((file) => ({
            attachment_url: file.name,
            attachment_mime_type: file.type,
            external_file_id: '',
        }));
        this._sessionState.addUserMessage(sessionId, text, messageFiles);
        this._sessionState.markStreaming(sessionId);
        const provider = await this._getSessionProvider(sessionId);
        let fileContentBlocks = [];
        if (files.length) {
            this._uppy.getPlugin('XHRUpload').setOptions({
                endpoint: this._urlsService.getUrl(this._urlsService.AGENT.FILE_UPLOAD, { provider }),
            });
            this._uppy.setMeta({ session_id: sessionId.toString() });
            this._isFileUploading = true;
            try {
                files.forEach((file) => {
                    this._uppy.addFile({
                        name: file.name,
                        type: file.type,
                        data: file,
                    });
                });
            }
            catch (err) {
                this._uppy.cancelAll();
                this._isFileUploading = false;
                this._sessionState.handleEvent(sessionId, {
                    type: 'error',
                    data: 'The file format is not supported. Supported file types include: .txt, .jpeg, .png, .pdf.',
                });
                return;
            }
            let uploadErrorStatus;
            const onUploadError = (_file, _error, response) => {
                if (uploadErrorStatus === undefined && response?.status) {
                    uploadErrorStatus = response.status;
                }
            };
            this._uppy.on('upload-error', onUploadError);
            try {
                const result = await this._uppy.upload().catch(() => undefined);
                if (uploadErrorStatus === 415) {
                    this._sessionState.handleEvent(sessionId, {
                        type: 'error',
                        data: 'The file format is not supported. Supported file types include: .txt, .jpeg, .png, .pdf.',
                    });
                    return;
                }
                if (uploadErrorStatus === 400) {
                    this._sessionState.handleEvent(sessionId, {
                        type: 'error',
                        data: 'There was an error uploading the file. Try again or contact support if the error persists.',
                    });
                    return;
                }
                fileContentBlocks = (result?.successful?.[0]?.response?.body?.files ?? []);
            }
            finally {
                this._uppy.off('upload-error', onUploadError);
                this._uppy.cancelAll();
                this._isFileUploading = false;
            }
        }
        const messageData = [
            ...fileContentBlocks.map((f) => ({ type: f.type, file_id: f.file_id })),
            { type: 'text', text },
        ];
        const encodedMessage = JSON.stringify({ type: 'user_input', data: messageData });
        const interactionId = this._interactionIds.get(sessionId);
        const params = new URLSearchParams({
            agent_session_id: sessionId.toString(),
            message: encodedMessage,
            user_id: window.USER.userId.toString(),
        });
        if (interactionId) {
            params.set('interaction_id', interactionId);
        }
        if (clientContext) {
            params.set('client_context', JSON.stringify(clientContext));
            this._lastClientContext.set(sessionId, clientContext);
        }
        params.set('provider', provider);
        const sseUrl = `${this._getBaseUrl()}/agent/new?${params.toString()}`;
        // Close any existing stream for this session
        this._closeEventSource(sessionId);
        this._openEventSource(sessionId, sseUrl);
    }
    async interrupt(sessionId) {
        const interactionId = this._interactionIds.get(sessionId);
        if (!interactionId) {
            return;
        }
        this._closeEventSource(sessionId);
        try {
            const url = `${this._getBaseUrl()}/agent/stop?agent_session_id=${sessionId}&interaction_id=${interactionId}`;
            await firstValueFrom(this._ajaxService.postJsonDataObserveBody(url, {}, false, true));
        }
        catch {
            // Ignore errors
        }
        this._sessionState.handleEvent(sessionId, { type: 'interrupted' });
    }
    async checkAndReconnect(sessionId, userId) {
        if (this.hasActiveConnection(sessionId)) {
            return;
        }
        const activeSessions = await this.getActiveSessions(userId);
        const interactionId = activeSessions[sessionId.toString()];
        if (!interactionId) {
            return;
        }
        this.setInteractionId(sessionId, interactionId);
        const chatState = this._sessionState.getChatState(sessionId);
        if (chatState.toolCallsToConfirm.length) {
            return;
        }
        const lastStreamId = this._sessionState.getLastMessageStreamId(sessionId);
        const clientContext = this._lastClientContext.get(sessionId);
        await this.reconnectToSession(sessionId, userId, interactionId, lastStreamId, clientContext);
    }
    disconnect(sessionId) {
        this._closeEventSource(sessionId);
        this._sessionState.markDisconnected(sessionId);
        this._lastClientContext.delete(sessionId);
        this._sessionProviders.delete(sessionId);
    }
    confirmToolCall(sessionId, userId, authorize, alwaysApply, toolCall) {
        let answer;
        if (authorize) {
            answer = alwaysApply ? 'allow_always' : 'allow';
        }
        else {
            answer = 'reject';
        }
        const confirmation = { tool_use_id: toolCall.tool_use_id, answer, reason: null };
        if (!this._pendingConfirmations.has(sessionId)) {
            this._pendingConfirmations.set(sessionId, []);
        }
        this._pendingConfirmations.get(sessionId).push(confirmation);
        this._sessionState.removeToolCallConfirmation(sessionId, toolCall.tool_use_id);
        const remaining = this._sessionState.getChatState(sessionId).toolCallsToConfirm;
        if (remaining.length === 0) {
            this._sendConfirmations(sessionId, userId);
        }
    }
    _openEventSource(sessionId, url, isReconnection = false) {
        const eventSource = new EventSource(url, { withCredentials: true });
        let receivedEvent = false;
        let reconnectionTimeout = null;
        if (isReconnection) {
            reconnectionTimeout = setTimeout(() => {
                if (!receivedEvent) {
                    this._closeEventSource(sessionId);
                    this._sessionState.handleEvent(sessionId, { type: 'done', data: [] });
                }
            }, 5000);
        }
        SSE_EVENT_TYPES.forEach((eventType) => {
            eventSource.addEventListener(eventType, (e) => {
                this._ngZone.run(() => {
                    if (eventType !== 'interaction_id') {
                        receivedEvent = true;
                        if (reconnectionTimeout) {
                            clearTimeout(reconnectionTimeout);
                            reconnectionTimeout = null;
                        }
                    }
                    this._handleSSEEvent(sessionId, eventType, e.data);
                });
            });
        });
        eventSource.onerror = () => {
            if (isReconnection && !receivedEvent) {
                return;
            }
            if (eventSource.readyState === EventSource.CLOSED) {
                return;
            }
            this._closeEventSource(sessionId);
            if (reconnectionTimeout) {
                clearTimeout(reconnectionTimeout);
            }
            this._sessionState.handleEvent(sessionId, { type: 'error', data: 'Connection lost. Please try again.' });
        };
        this._eventSources.set(sessionId, eventSource);
    }
    _closeEventSource(sessionId) {
        const existing = this._eventSources.get(sessionId);
        if (existing) {
            existing.close();
            this._eventSources.delete(sessionId);
        }
    }
    _handleSSEEvent(sessionId, eventType, data) {
        if (eventType === 'interaction_id') {
            this._interactionIds.set(sessionId, data);
            return;
        }
        if (eventType === 'done' || eventType === 'error') {
            this._closeEventSource(sessionId);
        }
        if (eventType === 'error_max_sessions') {
            this._closeEventSource(sessionId);
            this._sessionState.handleEvent(sessionId, {
                type: 'error',
                data: 'You have too many active sessions. Please wait for one to finish before starting a new one.',
            });
            return;
        }
        if (eventType === 'error_usage_limit') {
            this._closeEventSource(sessionId);
            this._sessionState.handleEvent(sessionId, { type: 'error', data });
            return;
        }
        if (eventType === 'error') {
            this._sessionState.handleEvent(sessionId, { type: 'error', data: UNKNOWN_ERROR_MESSAGE });
            return;
        }
        if (eventType === 'client_tool_request') {
            try {
                const parsed = JSON.parse(data);
                const requests = Array.isArray(parsed) ? parsed : parsed?.data;
                if (Array.isArray(requests)) {
                    this._handleClientToolRequest(sessionId, requests).catch(() => {
                        this._sessionState.handleEvent(sessionId, {
                            type: 'error',
                            data: 'Failed to execute client tool. Please try again.',
                        });
                    });
                }
            }
            catch {
                // ignore incorrect payloads
            }
            return;
        }
        try {
            const parsed = JSON.parse(data);
            this._sessionState.handleEvent(sessionId, parsed);
        }
        catch {
            // Ignore non-JSON event
        }
    }
    async _sendConfirmations(sessionId, userId) {
        const interactionId = this._interactionIds.get(sessionId);
        const confirmations = this._pendingConfirmations.get(sessionId) ?? [];
        this._pendingConfirmations.delete(sessionId);
        if (!interactionId || !confirmations.length) {
            return;
        }
        try {
            const params = new URLSearchParams({
                agent_session_id: sessionId.toString(),
                interaction_id: interactionId,
                user_id: userId.toString(),
            });
            const url = `${this._getBaseUrl()}/agent/confirm?${params.toString()}`;
            await firstValueFrom(this._ajaxService.postJsonDataObserveBody(url, { confirmations }, false, true));
            // Reconnect to the SSE stream to receive subsequent events
            if (!this.hasActiveConnection(sessionId)) {
                const lastStreamId = this._sessionState.getLastMessageStreamId(sessionId);
                const clientContext = this._lastClientContext.get(sessionId);
                await this.reconnectToSession(sessionId, userId, interactionId, lastStreamId, clientContext);
            }
        }
        catch {
            this._sessionState.handleEvent(sessionId, {
                type: 'error',
                data: 'Failed to send tool confirmation. Please try again.',
            });
        }
    }
    async _handleClientToolRequest(sessionId, requests) {
        const responses = await Promise.all(requests.map((req) => this._executeClientTool(req.name, req.input, req.tool_use_id)));
        await this._sendClientToolResponses(sessionId, responses);
        const interactionId = this._interactionIds.get(sessionId);
        if (interactionId && !this.hasActiveConnection(sessionId)) {
            const lastStreamId = this._sessionState.getLastMessageStreamId(sessionId);
            const clientContext = this._lastClientContext.get(sessionId);
            await this.reconnectToSession(sessionId, window.USER.userId, interactionId, lastStreamId, clientContext);
        }
    }
    async _executeClientTool(name, input, toolUseId) {
        try {
            switch (name) {
                case 'get_current_page_context':
                    return {
                        tool_use_id: toolUseId,
                        name,
                        result: await this._pageContextClientTool.execute(input),
                    };
                default:
                    return {
                        tool_use_id: toolUseId,
                        name,
                        error: { message: `Unknown client tool: ${name}` },
                    };
            }
        }
        catch (err) {
            return {
                tool_use_id: toolUseId,
                name,
                error: { message: err instanceof Error ? err.message : String(err) },
            };
        }
    }
    async _sendClientToolResponses(sessionId, responses) {
        const interactionId = this._interactionIds.get(sessionId);
        if (!interactionId)
            return;
        try {
            const params = new URLSearchParams({
                agent_session_id: sessionId.toString(),
                interaction_id: interactionId,
                user_id: window.USER.userId.toString(),
            });
            const url = `${this._getBaseUrl()}/agent/client_tool_response?${params.toString()}`;
            await firstValueFrom(this._ajaxService.postJsonDataObserveBody(url, { responses }, false, true));
        }
        catch {
            this._sessionState.handleEvent(sessionId, {
                type: 'error',
                data: 'Failed to send client tool response. Please try again.',
            });
        }
    }
    _getBaseUrl() {
        return window.location.hostname === 'localhost'
            ? `${window.location.protocol}//localhost:3003`
            : process.env.AI_PROXY_BASE_URL;
    }
}
AgentTransportService.ɵfac = function AgentTransportService_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || AgentTransportService)(); };
AgentTransportService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: AgentTransportService, factory: AgentTransportService.ɵfac, providedIn: 'root' });
